import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Info, Check } from "lucide-react";
import { useAnime } from "../context/AnimeContext";

/**
 * DiscoverSpotlight (Rotating Trending)
 * - Loops forever through rotation list
 * - Smooth crossfade
 * - Pause on hover + when tab hidden
 * - Adaptive overlay color so it works with ANY hero image
 * - Dots only (no "1/10" counter)
 */
export default function DiscoverSpotlight({
  list = [],
  label = "Trending",
  heightClass = "h-[420px]",
  intervalMs = 8500,
  maxItems = 10,
}) {
  const { addToFavorites, removeFromFavorites, isAnimeFavorited } = useAnime();

  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState("in"); // "in" | "out"

  // UI feedback states (like AnimeCard)
  const [favBusy, setFavBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const timerRef = useRef(null);
  const phaseRef = useRef(null);
  const toastRef = useRef(null);

  // Build rotation list (for now: top-rated)
  const rotationList = useMemo(() => {
    if (!Array.isArray(list) || !list.length) return [];

    const sorted = list
      .slice()
      .sort((a, b) => (b?.AverageRating || 0) - (a?.AverageRating || 0));

    const seen = new Set();
    const unique = [];
    for (const a of sorted) {
      const id = a?.AnimeID ?? a?.anime_id ?? a?.id;
      if (id == null) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      unique.push(a);
      if (unique.length >= maxItems) break;
    }

    return unique;
  }, [list, maxItems]);

  // Keep index valid
  useEffect(() => {
    if (!rotationList.length) return;
    setIndex((prev) => (prev >= rotationList.length ? 0 : prev));
  }, [rotationList.length]);

  // Auto-rotate (loops forever)
  useEffect(() => {
    const canRotate = rotationList.length > 1;
    const isHidden = typeof document !== "undefined" ? document.hidden : false;

    const clearTimers = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (phaseRef.current) clearTimeout(phaseRef.current);
      timerRef.current = null;
      phaseRef.current = null;
    };

    clearTimers();

    if (!canRotate || isPaused || isHidden) return;

    timerRef.current = setTimeout(() => {
      setPhase("out");
      phaseRef.current = setTimeout(() => {
        setIndex((prev) => (prev + 1) % rotationList.length);
        setPhase("in");
      }, 420);
    }, intervalMs);

    return clearTimers;
  }, [index, isPaused, intervalMs, rotationList.length]);

  // Visibility safety
  useEffect(() => {
    const onVis = () => setPhase("in");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Derived
  const spotlight = rotationList[index] || null;

  // MATCH AnimeCard EXACTLY: normalize id to string
  const rawId =
    spotlight?.AnimeID ??
    spotlight?.anime_id ??
    spotlight?.id ??
    spotlight?.AnimeId ??
    spotlight?.animeId ??
    null;

  const animeId = rawId != null ? String(rawId) : null;
  const title = spotlight?.Title || spotlight?.title || "Trending pick";

  // Reset transient feedback on slide change
  useEffect(() => {
    setFavBusy(false);
    setJustSaved(false);
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = null;
  }, [animeId]);

  const safeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch {}
      if (val.includes(","))
        return val
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      return [val].filter(Boolean);
    }
    return [];
  };

  const genres = spotlight ? safeArray(spotlight.Genres || spotlight.genres) : [];

  // Image fallback logic
  const sanitizedTitle = String(title).toLowerCase().replace(/[^a-z0-9]/gi, "").trim();
  const fallbackImage = sanitizedTitle ? `/images/${sanitizedTitle}.jpg` : null;

  const getImageSrc = () => {
    const url =
      spotlight?.PhotoUrl ||
      spotlight?.photo_url ||
      spotlight?.image ||
      spotlight?.image_url;

    if (!url || typeof url !== "string" || url.includes("example.com")) return fallbackImage;
    if (url.startsWith("http")) return url;
    return url.startsWith("/") ? url : `/${url.replace(/^\/+/, "")}`;
  };

  const cover = spotlight ? getImageSrc() : null;

  const handleImgError = (e) => {
    if (!fallbackImage) return;
    const fallbackAbs = window.location.origin + fallbackImage;
    if (e.currentTarget.src !== fallbackAbs) e.currentTarget.src = fallbackImage;
  };

  const isFavorite =
    animeId != null && typeof isAnimeFavorited === "function"
      ? isAnimeFavorited(animeId)
      : false;

  const handleFavoriteToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!spotlight || animeId == null || favBusy) return;

    setFavBusy(true);

    try {
      if (isFavorite) {
        await removeFromFavorites(animeId);
        setJustSaved(false);
      } else {
        await addToFavorites({
          ...spotlight,
          AnimeID: animeId,
          anime_id: animeId,
          id: animeId,
        });

        // “Saved” feedback like AnimeCard (small confirmation)
        setJustSaved(true);
        if (toastRef.current) clearTimeout(toastRef.current);
        toastRef.current = setTimeout(() => setJustSaved(false), 1400);
      }
    } catch (err) {
      console.error("Favorite toggle error:", err);
    } finally {
      setFavBusy(false);
    }
  };

  // Hook must NOT be after an early return — keep it here always.
  const accent = useMemo(() => {
    const palette = [
      { a: "rgba(16,185,129,0.16)", b: "rgba(34,211,238,0.10)" }, // emerald/cyan
      { a: "rgba(99,102,241,0.14)", b: "rgba(236,72,153,0.08)" }, // indigo/pink
      { a: "rgba(34,211,238,0.14)", b: "rgba(16,185,129,0.10)" }, // cyan/emerald
      { a: "rgba(250,204,21,0.10)", b: "rgba(99,102,241,0.10)" }, // amber/indigo
    ];

    const key =
      animeId == null ? index : Number(String(animeId).replace(/\D/g, "")) || index;

    return palette[Math.abs(key) % palette.length];
  }, [animeId, index]);

  // Early return AFTER hooks are declared
  if (!spotlight || animeId == null) return null;

  return (
    <section className="mb-10">
      <div
        className={`relative ${heightClass} rounded-[28px] overflow-hidden`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Crossfade wrapper */}
        <div
          className={[
            "absolute inset-0",
            "transition-opacity duration-[420ms] ease-out",
            phase === "out" ? "opacity-0" : "opacity-100",
          ].join(" ")}
        >
          {/* BLURRED BACKPLATE */}
          {cover ? (
            <img
              src={cover}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover scale-[1.08] blur-[18px] opacity-55"
              loading="lazy"
              onError={handleImgError}
              style={{ transform: "translateZ(0)" }}
            />
          ) : (
            <div className="absolute inset-0 bg-white/5" />
          )}

          {/* Neutral cinematic wash (works for ALL artwork) */}
          <div className="absolute inset-0 bg-black/45" />

          {/* SHARP FOREGROUND */}
          {cover ? (
            <img
              src={cover}
              alt={title}
              className={[
                "absolute inset-0 h-full w-full object-contain",
                "transition-transform duration-[900ms] ease-out",
                phase === "in" ? "scale-[1.01]" : "scale-[1.03]",
              ].join(" ")}
              loading="eager"
              onError={handleImgError}
              style={{ transform: "translateZ(0)", imageRendering: "auto" }}
            />
          ) : null}

          {/* Vignette + contrast (neutral) */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_35%,rgba(0,0,0,0.10),rgba(0,0,0,0.82))]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/88 via-black/45 to-transparent" />

          {/* Adaptive accent (soft, doesn’t overpower images) */}
          <div
            className="absolute inset-0 opacity-90"
            style={{
              background: `radial-gradient(circle at 28% 28%, ${accent.a}, transparent 45%), radial-gradient(circle at 78% 22%, ${accent.b}, transparent 48%)`,
            }}
          />
        </div>

        {/* Content */}
        <div
          className={[
            "relative z-10 h-full flex items-end",
            "transition-opacity duration-[420ms] ease-out",
            phase === "out" ? "opacity-0" : "opacity-100",
          ].join(" ")}
        >
          <div className="w-full p-5 sm:p-8 md:p-10">
            <p className="text-[11px] tracking-[0.35em] uppercase text-white/70">
              {label}
            </p>

            <h2 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.05]">
              {title}
            </h2>

            {genres.length ? (
              <p className="mt-3 text-sm text-white/70">
                {genres.slice(0, 5).join(" • ")}
              </p>
            ) : null}

            <div className="mt-6 flex flex-wrap gap-3 items-center">
              {/* Details -> glass */}
              <Link
                to={`/anime/${animeId}`}
                className={[
                  "flex items-center gap-2 px-5 py-2.5 rounded-xl",
                  "border border-white/20 bg-white/7 hover:bg-white/10",
                  "backdrop-blur-md transition-colors",
                  "text-white font-semibold shadow-[0_10px_35px_rgba(0,0,0,0.28)]",
                ].join(" ")}
                style={{ textDecoration: "none" }}
              >
                <Info size={16} /> Details
              </Link>

              {/* Favorites -> icon-only (AnimeCard-like hover/active feedback) */}
              <button
                ref={toastRef}
                type="button"
                onClick={handleFavoriteToggle}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                disabled={favBusy}
                aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
                className={[
                  "h-11 w-11 rounded-xl grid place-items-center",
                  "border backdrop-blur-md transition",
                  "shadow-[0_10px_35px_rgba(0,0,0,0.28)]",
                  "active:scale-95",
                  favBusy ? "opacity-70 cursor-not-allowed" : "cursor-pointer",
                  isFavorite
                    ? "border-white/25 bg-white/10 hover:bg-white/14"
                    : "border-white/20 bg-white/7 hover:bg-white/10",
                ].join(" ")}
              >
                <Bookmark
                  size={18}
                  className={
                    isFavorite
                      ? "text-pink-400 fill-pink-400"
                      : "text-white/85"
                  }
                />
              </button>

              {/* always show feedback when saved */}
              {isFavorite || justSaved ? (
                <span className="inline-flex items-center gap-2 text-xs text-white/70">
                  <Check size={14} className="text-white/80" />
                  Saved
                </span>
              ) : null}
            </div>

            <div className="mt-6 h-px w-full bg-gradient-to-r from-white/0 via-white/25 to-white/0" />
          </div>
        </div>
      </div>
    </section>
  );
}
